
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

export interface Section {
  id: string;
  label: string;
}

@Component
export default class SectionNav extends Vue {
  @Prop({ default: () => [] }) readonly sections!: Section[];

  @Prop({ default: "" }) readonly scrollContainerSelector!: string;

  // offset from top in pixels (header height)
  @Prop({ default: 60 }) readonly scrollOffset!: number;

  // debug logs
  @Prop({ default: false }) readonly debug!: boolean;

  // state
  showNav = false;
  activeIndex: number | null = null;
  private scrollContainer: HTMLElement | null = null;
  private listenerAttachedTo: "window" | HTMLElement | null = null;

  mounted(): void {
    this.$nextTick(() => {
      if (this.debug)
        console.log("[SectionNav] mounted - sections:", this.sections);
      this.initScrollContainer();
      this.attachScrollListener();
      this.onScroll();
    });

    // Click outside listener
    document.addEventListener("click", this.handleClickOutside);
  }

  beforeDestroy(): void {
    this.removeScrollListener();
    document.removeEventListener("click", this.handleClickOutside);
  }

  @Watch("sections", { deep: true })
  onSectionsChanged(): void {
    if (this.debug)
      console.log("[SectionNav] sections changed:", this.sections);

    this.$nextTick(() => {
      this.initScrollContainer();
      this.attachScrollListener();
      this.onScroll();
    });
  }

  // Handle click outside to close nav
  handleClickOutside(event: Event): void {
    const target = event.target as Node;
    const sectionNavigator = this.$refs.sectionNavigator as HTMLElement;

    if (
      this.showNav &&
      sectionNavigator &&
      !sectionNavigator.contains(target)
    ) {
      this.showNav = false;
    }
  }

  toggleNav(): void {
    this.showNav = !this.showNav;
  }

  scrollToTop(): void {
    this.ensureScrollContainer();
    const root = this.getRootScrollingElement();
    if (this.scrollContainer && this.scrollContainer !== root) {
      this.scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    this.showNav = false;
    setTimeout(() => this.onScroll(), 250);
  }

  // Scroll to a specific section id
  scrollToSection(id: string): void {
    this.ensureScrollContainer();
    const el = document.getElementById(id);
    if (!el) {
      if (this.debug)
        console.warn(
          `[SectionNav] scrollToSection: element not found for id="${id}"`
        );
      return;
    }

    const root = this.getRootScrollingElement();
    let targetTop = 0;

    if (this.scrollContainer && this.scrollContainer !== root) {
      const containerRect = this.scrollContainer.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const relativeTop =
        this.scrollContainer.scrollTop + (elRect.top - containerRect.top);
      targetTop = Math.max(0, Math.round(relativeTop - this.scrollOffset));
      this.scrollContainer.scrollTo({ top: targetTop, behavior: "smooth" });
    } else {
      const elRect = el.getBoundingClientRect();
      const absoluteTop = window.scrollY + elRect.top - this.scrollOffset;
      targetTop = Math.max(0, Math.round(absoluteTop));
      window.scrollTo({ top: targetTop, behavior: "smooth" });
    }

    if (this.debug)
      console.log(
        `[SectionNav] scrollToSection #${id} -> targetTop=${targetTop}`
      );

    this.showNav = false;
    setTimeout(() => this.onScroll(), 250);
  }

  // ---------- Utilities ----------

  private ensureScrollContainer(): void {
    if (this.scrollContainer) return;
    this.initScrollContainer();
    this.attachScrollListener();
  }

  private getRootScrollingElement(): HTMLElement {
    return (document.scrollingElement ||
      document.documentElement) as HTMLElement;
  }

  private initScrollContainer(): void {
    if (this.scrollContainerSelector) {
      const found = document.querySelector(
        this.scrollContainerSelector
      ) as HTMLElement | null;
      if (found) {
        this.scrollContainer = found;
        if (this.debug)
          console.log(
            "[SectionNav] using provided scroll container:",
            this.scrollContainerSelector,
            found
          );
        return;
      } else {
        if (this.debug)
          console.warn(
            "[SectionNav] scrollContainerSelector provided but not found:",
            this.scrollContainerSelector
          );
      }
    }

    if (this.sections && this.sections.length > 0) {
      for (const s of this.sections) {
        const el = document.getElementById(s.id);
        if (el) {
          this.scrollContainer = this.findScrollableParent(el);
          if (this.debug)
            console.log(
              "[SectionNav] autodetected scroll container from element:",
              s.id,
              this.scrollContainer
            );
          return;
        }
      }
    }

    // fallback to the root (page) scrolling element
    this.scrollContainer = this.getRootScrollingElement();
    if (this.debug) console.log("[SectionNav] fallback to root scroll element");
  }

  private findScrollableParent(el: Element): HTMLElement {
    let parent: HTMLElement | null = el.parentElement;
    while (parent && parent !== document.body) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;
      const canScroll =
        (overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowY === "overlay") &&
        parent.scrollHeight > parent.clientHeight;
      if (canScroll) return parent;
      parent = parent.parentElement;
    }
    return this.getRootScrollingElement();
  }

  // Attach scroll listener to either the scroll container or the window
  private attachScrollListener(): void {
    this.removeScrollListener();
    this.ensureScrollContainer();
    const root = this.getRootScrollingElement();
    if (this.scrollContainer && this.scrollContainer !== root) {
      this.scrollContainer.addEventListener("scroll", this.onScroll, {
        passive: true,
      });
      this.listenerAttachedTo = this.scrollContainer;
      if (this.debug)
        console.log(
          "[SectionNav] attached scroll listener to container",
          this.scrollContainer
        );
    } else {
      window.addEventListener("scroll", this.onScroll, { passive: true });
      this.listenerAttachedTo = "window";
      if (this.debug)
        console.log("[SectionNav] attached scroll listener to window");
    }
  }

  // Remove any existing scroll listener
  private removeScrollListener(): void {
    if (this.listenerAttachedTo === "window") {
      window.removeEventListener("scroll", this.onScroll as EventListener);
    } else if (this.listenerAttachedTo instanceof HTMLElement) {
      this.listenerAttachedTo.removeEventListener(
        "scroll",
        this.onScroll as EventListener
      );
    }
    this.listenerAttachedTo = null;
  }

  // Scroll spy
  onScroll = (): void => {
    // Ensure scroll container is fresh
    this.ensureScrollContainer();
    const offset = this.scrollOffset;
    let currentIndex: number | null = null;

    const root = this.getRootScrollingElement();
    const useContainer = this.scrollContainer && this.scrollContainer !== root;

    for (let i = 0; i < this.sections.length; i++) {
      const s = this.sections[i];
      const el = document.getElementById(s.id);
      if (!el) continue;

      let distanceFromTop: number;
      if (useContainer && this.scrollContainer) {
        const containerRect = this.scrollContainer.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        distanceFromTop = elRect.top - containerRect.top;
      } else {
        const elRect = el.getBoundingClientRect();
        distanceFromTop = elRect.top;
      }

      if (distanceFromTop <= offset + 1) {
        currentIndex = i;
      }
    }

    if (currentIndex === null) {
      const atTop =
        useContainer && this.scrollContainer
          ? this.scrollContainer.scrollTop <= this.scrollOffset
          : window.scrollY <= offset;
      if (atTop) currentIndex = null;
    }

    this.activeIndex = currentIndex;
  };
}
